(() => {
  'use strict';
  if (window.__covermaticDraftInstalled) return;
  window.__covermaticDraftInstalled = true;
  const KEY = 'covermatic.reviewable-draft.v1';
  const TTL = 2 * 60 * 60 * 1000;
  let cleanup = () => {};
  const runtime = () => window.CovermaticRuntime || window.AlphaRuntime;
  const field = id => document.getElementById(id);
  function clean(value, limit) {
    if (value === undefined) return '';
    if (typeof value !== 'string' || value.length > limit) throw Error('invalid_draft');
    return value.trim();
  }
  function normalize(input) {
    if (!input || typeof input !== 'object' || Array.isArray(input)) throw Error('invalid_draft');
    const draft = { artist: clean(input.artist, 150), title: clean(input.title, 150),
      concept: clean(input.concept, 4000), src: clean(input.src || 'agent', 40) };
    const id = input.design_id || input.premade_id;
    if (id !== undefined && id !== '') {
      if (typeof id !== 'string' || !/^\d{9,20}$/.test(id)) throw Error('invalid_design');
      draft.design_id = id;
    }
    if (!/^[a-z0-9_-]{1,40}$/i.test(draft.src)) throw Error('invalid_source');
    return draft;
  }
  function read() {
    const hash = new URLSearchParams(location.hash.slice(1));
    if (hash.has('brief')) {
      if (hash.getAll('brief').length !== 1) throw Error('invalid_draft');
      const raw = hash.get('brief');
      if (raw.length > 12000) throw Error('invalid_draft');
      return normalize(JSON.parse(raw));
    }
    const query = new URLSearchParams(location.search);
    if (['artist', 'title', 'concept'].some(key => query.has(key))) {
      for (const key of ['artist', 'title', 'concept', 'premade_id', 'design_id']) {
        if (query.getAll(key).length > 1) throw Error('invalid_draft');
      }
      return normalize(Object.fromEntries(['artist', 'title', 'concept', 'premade_id', 'src']
        .filter(key => query.has(key)).map(key => [key, query.get(key)])));
    }
    if (query.get('draft') === '1') {
      const saved = JSON.parse(sessionStorage.getItem(KEY) || 'null');
      if (saved && Number.isFinite(saved.time) && Date.now() - saved.time >= 0 && Date.now() - saved.time < TTL) {
        return normalize(saved.brief);
      }
      sessionStorage.removeItem(KEY);
    }
    return null;
  }
  function save(brief) {
    try { sessionStorage.setItem(KEY, JSON.stringify({ time: Date.now(), brief })); } catch (_) {}
  }
  function notice(message) {
    const form = field('cm-form');
    if (!form) return null;
    const box = document.createElement('section');
    box.id = 'covermatic-draft-review';
    box.setAttribute('aria-label', 'Review your artwork draft');
    box.style.cssText = 'border:1px solid #c9c7bf;border-radius:12px;background:#fffdf7;padding:16px;margin:16px 0;overflow-wrap:anywhere';
    const heading = document.createElement('h2'); heading.textContent = 'Review your artwork draft';
    const text = document.createElement('p'); text.textContent = message;
    box.append(heading, text); form.before(box); return box;
  }
  async function start() {
    let brief;
    try { brief = read(); } catch (_) { notice('This draft link could not be loaded. Enter your brief below; nothing has been submitted.'); return; }
    if (!brief) return;
    save(brief);
    for (let tries = 0; tries < 100; tries++) {
      if (field('artist') && runtime()?.getService?.('modals')?.templateBrowser) break;
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    if (!field('artist') || !field('title') || !field('concept')) return;
    cleanup();
    if (runtime()?.State) runtime().State.editMode = false;
    runtime()?.getService?.('flow')?.startCustom?.({ scroll: false });
    runtime()?.getService?.('form')?.clearTemplate?.();
    for (const key of ['artist', 'title', 'concept']) {
      field(key).value = brief[key];
      field(key).dispatchEvent(new Event('input', { bubbles: true }));
      field(key).dispatchEvent(new Event('change', { bubbles: true }));
    }
    let direction = '';
    if (brief.design_id) {
      try {
        const response = await fetch('/apps/covermatic/signature-design/' + encodeURIComponent(brief.design_id), { headers: { Accept: 'application/json' } });
        if (!response.ok) throw Error('unavailable');
        const design = await response.json();
        const image = new URL(design.template_image || design.image);
        if (String(design.product_id) !== brief.design_id || typeof design.title !== 'string' ||
            typeof design.handle !== 'string' || !/^[a-z0-9-]+$/.test(design.handle) ||
            image.protocol !== 'https:' || !['cdn.shopify.com', 'dontsleepgfx.com', 'www.dontsleepgfx.com'].includes(image.hostname) || image.username || image.password) throw Error('invalid_design');
        const browser = runtime()?.getService?.('modals')?.templateBrowser;
        if (!browser) throw Error('studio_not_ready');
        await browser.selectTemplate({ id: brief.design_id, handle: design.handle, title: design.title, imageUrl: image.href, kind: 'premade' });
        direction = design.title;
      } catch (_) { direction = 'The selected direction could not be loaded. Choose a direction below before generating.'; delete brief.design_id; }
    }
    for (const key of ['artist', 'title', 'concept']) {
      field(key).value = brief[key];
      field(key).dispatchEvent(new Event('input', { bubbles: true }));
      field(key).dispatchEvent(new Event('change', { bubbles: true }));
    }
    const box = notice('Your draft is prefilled below. Review every field and the selected direction. A generation or edit currently uses 5 credits per attempt. Nothing has been submitted or charged.');
    if (!box) return;
    if (direction) { const text = document.createElement('p'); text.textContent = 'Direction: ' + direction; box.append(text); }
    if (!runtime()?.Config?.customerId) {
      const back = new URL('/pages/ai-cover-art-generator', location.origin);
      back.searchParams.set('draft', '1'); back.searchParams.set('src', brief.src);
      const link = document.createElement('a');
      link.href = '/account/login?return_url=' + encodeURIComponent(back.pathname + back.search);
      link.textContent = 'Sign in to continue with this draft';
      link.style.cssText = 'display:inline-block;padding:10px 0;min-height:44px'; box.append(link);
    }
    let remember = true;
    const keep = () => {
      if (!remember) return;
      try {
        const next = normalize({ ...brief, artist: field('artist').value, title: field('title').value, concept: field('concept').value });
        const selected = runtime()?.State?.template;
        if (selected && /^\d{9,20}$/.test(String(selected.id))) next.design_id = String(selected.id);
        else if (!selected) delete next.design_id;
        save(next);
      } catch (_) {}
    };
    for (const key of ['artist', 'title', 'concept']) field(key).addEventListener('input', keep);
    window.addEventListener('pagehide', keep);
    cleanup = () => {
      remember = false;
      for (const key of ['artist', 'title', 'concept']) field(key).removeEventListener('input', keep);
      window.removeEventListener('pagehide', keep);
      box.remove();
    };
    const forget = document.createElement('button'); forget.type = 'button'; forget.textContent = 'Forget saved draft';
    forget.style.cssText = 'display:block;min-height:44px;padding:8px 0';
    forget.addEventListener('click', () => { remember = false; try { sessionStorage.removeItem(KEY); } catch (_) {} forget.textContent = 'Saved draft cleared'; forget.disabled = true; });
    box.append(forget);
    save(brief);
    const cleanURL = new URL(location.href);
    for (const key of ['artist', 'title', 'concept', 'design_id']) cleanURL.searchParams.delete(key);
    if (cleanURL.hash.startsWith('#brief=')) cleanURL.hash = '';
    cleanURL.searchParams.set('draft', '1'); cleanURL.searchParams.set('src', brief.src);
    history.replaceState(null, '', cleanURL.pathname + cleanURL.search + cleanURL.hash);
    document.dispatchEvent(new CustomEvent('covermatic:draft-ready', { detail: { selected: Boolean(brief.design_id) } }));
  }
  let pending = Promise.resolve();
  const schedule = () => { pending = pending.then(start).catch(() => { notice('This draft could not be restored. Enter your brief below; nothing has been submitted.'); }); };
  window.addEventListener('hashchange', () => {
    if (new URLSearchParams(location.hash.slice(1)).has('brief')) schedule();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule, { once: true });
  else schedule();
})();
